import { useMutation } from "@tanstack/react-query";
import { api } from "jiffy-api";

interface EditCartParams {
  assemblyGroupId: string;
  variants: Record<string, { quantity: number }>;
  placements: Array<{
    dtfArtwork: {
      id: number;
      enhancementTier: string;
      removeBackground: boolean;
      enhancementRequestNote?: string;
      jiffyDesignServices?: boolean;
      editImageManual?: boolean;
      pinningSessionId?: string;
    };
    width: number;
    height: number;
    position: string;
  }>;
}

export function useEditCartMutation() {
  return useMutation({
    mutationFn: async (params: EditCartParams) => {
      const formData = new FormData();

      // Add variants
      Object.entries(params.variants).forEach(([variantId, variant]) => {
        formData.append(`variants[${variantId}][quantity]`, variant.quantity.toString());
      });

      // Add placements
      params.placements.forEach((placement, index) => {
        formData.append(
          `placements[${index}][dtf_artwork][id]`,
          placement.dtfArtwork.id.toString()
        );
        formData.append(`placements[${index}][width]`, placement.width.toString());
        formData.append(`placements[${index}][height]`, placement.height.toString());
        formData.append(
          `placements[${index}][dtf_artwork][enhancement_tier]`,
          placement.dtfArtwork.enhancementTier
        );

        if (placement.dtfArtwork.enhancementRequestNote !== undefined) {
          const shouldIncludeComment =
            placement.dtfArtwork.enhancementTier === "custom" ||
            (placement.dtfArtwork.jiffyDesignServices &&
              placement.dtfArtwork.enhancementTier === "full_enhancements") ||
            (placement.dtfArtwork.enhancementRequestNote &&
              placement.dtfArtwork.enhancementRequestNote.trim() !== "");

          if (shouldIncludeComment) {
            formData.append(
              `placements[${index}][dtf_artwork][comment]`,
              placement.dtfArtwork.enhancementRequestNote || ""
            );
          }
        }
        formData.append(`placements[${index}][position]`, placement.position);
        formData.append(
          `placements[${index}][dtf_artwork][remove_background]`,
          placement.dtfArtwork.removeBackground.toString()
        );

        if (placement.dtfArtwork.editImageManual !== undefined) {
          formData.append(
            `placements[${index}][dtf_artwork][edit_image_manual]`,
            placement.dtfArtwork.editImageManual.toString()
          );
        }

        if (placement.dtfArtwork.pinningSessionId) {
          formData.append(
            `placements[${index}][dtf_artwork][pinning_session_id]`,
            placement.dtfArtwork.pinningSessionId
          );
        }
      });

      // @ts-ignore - TypeScript types are incorrect for this API
      return api.printOnDemand.updateMadeOrder({ asgid: params.assemblyGroupId }, formData, {
        raw: true,
        useAxiosForRequest: true,
        headers: { "Content-Type": undefined }
      });
    }
  });
}
